// De twee kaarten van de trendlaag voor de kaart-in-kaart (nieuw/kaartlaag.tsx):
//   "laagtrend"     het paneel van een trend: Waarom nu bovenaan, de meters, de
//                   ontwikkelingen (regel R4) direct daaronder
//   "ontwikkeling"  de ontwikkelingenkaart: werkzin, soort en datum, en de
//                   bevindingen met hun citaat en afzender
// Klik op een ontwikkeling in de trendkaart legt de ontwikkelingenkaart erover.
// In plan-modus (Plan 1.3 "Zelf zoeken", planmodus.ts) staat per ontwikkeling of
// ze al gekozen is, en anders "+ Toevoegen aan 1.3"; op de ontwikkelingenkaart
// zit die knop in de voet, met daarna "Toegevoegd · ongedaan maken".
// Daarnaast de lader van één bevinding (BevindingLader, voor de soort
// "bevinding" in trends/kaart.tsx bij aan-stand): vorm als de ontwikkelingenkaart,
// met in de voet waar ze bij hoort.

import { Check, ExternalLink, Plus, Undo2 } from "lucide-react";

import { api, type TrendlaagKaartScores, type TrendlaagTrend } from "../../lib/api";
import { actieveSector } from "../../lib/sectorkeuze";
import { useResource } from "../../lib/useResource";
import { Icoon, Info, Knop, LaadRegels, LegeStaat, Pil } from "../../stijl";
import { type KaartComponent, registreerKaart, useOpenKaart } from "../kaartlaag";
import { actievePlanPropositie } from "../plan/propositie";
import { haalWeg, MAX_ZELF_PLAN, statusVan, usePlanModus, voegToe } from "./planmodus";
import { beweging, maandJaar, ontwMeta } from "./woorden";

// De trendkaart leest uit dezelfde lijst als de pagina (één aanroep per sector, gedeeld).
// Een mislukte aanroep blijft niet bewaard: "Opnieuw proberen" vraagt het echt opnieuw.
const cache = new Map<string, Promise<TrendlaagTrend[]>>();
export const trendsVoorKaart = (sector: string = actieveSector() ?? "ict") => {
  let lijst = cache.get(sector);
  if (!lijst) {
    lijst = api.trendlaag(sector).then((r) => r.trends);
    lijst.catch(() => cache.delete(sector));
    cache.set(sector, lijst);
  }
  return lijst;
};
export const leegKaartCache = () => {
  cache.clear();
};

/** De trend, uit de sector van de schermen en die van het plan als beheer daar een
 *  andere propositie heeft gekozen. Een mislukte sector telt pas als de trend niet in
 *  de andere staat: dan weten we niet of hij bestaat. */
async function trendVanDezeWeergave(id: string): Promise<TrendlaagTrend | null> {
  const eigen = actieveSector() ?? "ict";
  const plan = actievePlanPropositie();
  const sectoren = plan.voorbeeld || plan.sector === eigen ? [eigen] : [eigen, plan.sector];
  const uit = await Promise.allSettled(sectoren.map((s) => trendsVoorKaart(s)));
  for (const r of uit) {
    const t = r.status === "fulfilled" ? r.value.find((x) => x.id === id) : undefined;
    if (t) return t;
  }
  const fout = uit.find((r) => r.status === "rejected");
  if (fout) throw (fout as PromiseRejectedResult).reason;
  return null;
}

/** Plan-modus, klein (rij in de lijst of op de trendkaart): gekozen, toegevoegd
 *  met ongedaan maken, of de knop "+ Toevoegen aan 1.3". */
export function PlanStatus({ id, titel, knop = true }: { id: string; titel: string; knop?: boolean }) {
  const p = usePlanModus();
  if (!p.actief) return null;
  const st = statusVan(p, id);
  if (st === "gekozen") return <span className="tl-plan__st tl-plan__st--grijs"><Pil>Gekozen in Swipen</Pil></span>;
  if (st === "toegevoegd")
    return (
      <span className="tl-plan__st">
        <Pil toon="accent">
          <Icoon als={Check} klein /> Toegevoegd
        </Pil>
        <button type="button" className="tl-plan__terug" aria-label={`Ongedaan maken: ${titel}`} title="Ongedaan maken" onClick={() => haalWeg(id)}>
          <Icoon als={Undo2} klein />
        </button>
      </span>
    );
  if (!knop) return null;
  return (
    <span className="tl-plan__st">
      <Knop
        variant="zacht"
        icoon={Plus}
        className="tl-plan__knop"
        disabled={st === "vol"}
        title={st === "vol" ? `Al ${MAX_ZELF_PLAN} van ${MAX_ZELF_PLAN}: haal er eerst een weg in het plan` : undefined}
        aria-label={`Toevoegen aan 1.3: ${titel}`}
        onClick={() => voegToe(id)}
      >
        Toevoegen aan 1.3
      </Knop>
    </span>
  );
}

/** Plan-modus, de voet van de ontwikkelingenkaart: één knop, daarna wat er gebeurde. */
function PlanVoet({ id }: { id: string }) {
  const p = usePlanModus();
  if (!p.actief) return null;
  const st = statusVan(p, id);
  const n = p.zelf.length;
  return (
    <div className="tl-plan__voet" aria-live="polite">
      {st === "gekozen" ? (
        <>
          <Pil>Gekozen in Swipen</Pil>
          <span className="s-meta">Deze staat al in jullie keuze.</span>
        </>
      ) : st === "toegevoegd" ? (
        <>
          <Pil toon="accent">
            <Icoon als={Check} klein /> Toegevoegd aan 1.3 · {n} van {MAX_ZELF_PLAN}
          </Pil>
          <Knop variant="link" onClick={() => haalWeg(id)}>
            Ongedaan maken
          </Knop>
        </>
      ) : (
        <>
          <Knop variant="primair" icoon={Plus} disabled={st === "vol"} onClick={() => voegToe(id)}>
            Toevoegen aan 1.3
          </Knop>
          <span className="s-meta">
            {st === "vol" ? `${MAX_ZELF_PLAN} van ${MAX_ZELF_PLAN} vol: haal er eerst een weg in het plan` : `nog ${MAX_ZELF_PLAN - n} van ${MAX_ZELF_PLAN} vrij`}
          </span>
        </>
      )}
    </div>
  );
}

/** Een score als in de oude trendkaart (versie 1): label, groot cijfer met één
 *  decimaal en een komma, een balkje van 0 tot 10. Zonder getal staat er "nog niet
 *  gemeten": dan is er onder deze trend nog geen bevinding met deze score. */
const komma = (n: number) => n.toFixed(1).replace(".", ",");
const SCORES = [
  { as: "trending", label: "Trending", toon: "blauw" },
  { as: "impact", label: "Impact", toon: "groen" },
  { as: "urgency", label: "Urgentie", toon: "oranje" },
] as const;

function Scores({ k }: { k: TrendlaagKaartScores | undefined }) {
  const n = k?.n ?? 0;
  return (
    <section className="tl-scores" aria-label="Scores">
      <h4 className="tl-blokkop">
        Scores
        <Info
          label="Uitleg bij de scores"
          tekst="Elke bevinding krijgt een score van 0 tot 10 voor Trending, Impact en Urgentie. Hier staat het gemiddelde van de bevindingen onder de ontwikkelingen van deze trend."
        />
      </h4>
      <div className="tl-scores__rij">
        {SCORES.map((s) => {
          const v = k?.[s.as];
          const gemeten = typeof v === "number";
          return (
            <div key={s.as} className={`tl-score tl-score--${s.toon}`} data-gemeten={gemeten}>
              <span className="tl-score__label">{s.label}</span>
              <span className="tl-score__cijfer">{gemeten ? komma(v) : "nog niet gemeten"}</span>
              <span className="tl-score__balk" aria-hidden="true">
                <i style={{ width: `${gemeten ? Math.max(0, Math.min(10, v)) * 10 : 0}%` }} />
              </span>
            </div>
          );
        })}
      </div>
      <p className="s-meta tl-scores__basis">
        {n > 0 ? `Gemiddeld over ${n} ${n === 1 ? "bevinding" : "bevindingen"}` : "Er is onder deze trend nog geen bevinding met een score."}
      </p>
    </section>
  );
}

/** "Impact op onderwijs" van de sterkste bevinding (hoogste Impact) onder de trend:
 *  wat het nu betekent en wat het later betekent. */
function OnderwijsImpact({ k }: { k: TrendlaagKaartScores | undefined }) {
  const o = k?.onderwijs;
  const blok = (kop: string, tekst: string | null | undefined, sterk: boolean) => (
    <div className={`tl-onderwijs__blok${sterk ? " tl-onderwijs__blok--nu" : ""}`}>
      <span className="s-meta tl-onderwijs__kop">{kop}</span>
      {tekst ? <p className="tl-onderwijs__tekst">{tekst}</p> : <p className="s-meta">nog niet gemeten</p>}
    </div>
  );
  return (
    <section className="tl-onderwijs" aria-label="Impact op onderwijs">
      <h4 className="tl-blokkop">Impact op onderwijs</h4>
      {blok("Nu", o?.nu, true)}
      {blok("Later", o?.straks, false)}
    </section>
  );
}

function TrendInhoud({ t }: { t: TrendlaagTrend }) {
  const open = useOpenKaart();
  const b = beweging(t.bewegingLabel);
  const soorten = Object.entries(t.bronSoorten ?? {}).filter(([, n]) => n > 0);
  return (
    <div className="tl-kaart">
      {/* De labels, zoals bovenaan de oude trendkaart. */}
      <div className="tl-labels">
        <Pil>{t.soortTrend === "arbeidsmarkt" ? "arbeidsmarkt" : "vak"}</Pil>
        {t.opkomend && <Pil toon="blauw">opkomend</Pil>}
        {b && <Pil toon={b.toon}>{b.woord}</Pil>}
      </div>
      {/* De samenvatting met de bronnen. */}
      <p className="tl-sub">{t.subtekst}</p>
      <ul className="tl-meters">
        <li>
          <b>{t.bronnen ?? 0}</b> bronnen
          {soorten.length > 0 && <span className="s-meta"> ({soorten.map(([s, n]) => `${n} ${s}`).join(", ")})</span>}
        </li>
        <li>
          <b>{t.ontwikkelingen.length}</b> ontwikkelingen
        </li>
        <li>
          <b>{t.nBevindingen ?? 0}</b> bevindingen
        </li>
      </ul>
      {t.waaromNu && (
        <p className="tl-waaromnu">
          <span className="s-meta">Waarom nu</span> {t.waaromNu}
        </p>
      )}
      <WaaromNuRegels trendId={t.id} />
      <Scores k={t.kaartScores} />
      <OnderwijsImpact k={t.kaartScores} />
      <h4 className="tl-blokkop">Ontwikkelingen</h4>
      {t.ontwikkelingen.length === 0 ? (
        <p className="s-meta">Deze trend draagt losse bevindingen, nog zonder eigen ontwikkeling.</p>
      ) : (
        <ul className="tl-ontw">
          {t.ontwikkelingen.map((o) => (
            <li key={o.id} className="tl-plan__rij">
              <button type="button" className="tl-ontw__rij" onClick={() => open({ soort: "ontwikkeling", id: o.id, titel: o.titel })}>
                <span className="tl-ontw__titel">{o.titel}</span>
                {o.werkzin && <span className="tl-ontw__werkzin">{o.werkzin}</span>}
                <span className="s-meta">{ontwMeta(o)}</span>
              </button>
              <PlanStatus id={o.id} titel={o.titel} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** W1: de context-bevindingen van de trend, getal vooraan en vet; klik opent de
 *  kaart van die bevinding. */
function WaaromNuRegels({ trendId }: { trendId: string }) {
  const open = useOpenKaart();
  const r = useResource(() => api.trendlaagWaaromNu(trendId), [trendId]);
  const d = r.data;
  if (!d || d.regels.length === 0) return null;
  return (
    <div className="tl-wn">
      <ul className="tl-wn__lijst" aria-label="Waarom nu-regels">
        {d.regels.map((w) => (
          <li key={w.id}>
            <button type="button" className="tl-wn__rij" onClick={() => open({ soort: "bevinding", id: w.id, titel: w.titel ?? undefined })}>
              {w.getal && <b className="tl-wn__getal">{w.getal}</b>}
              <span className="tl-wn__zin">{w.titel}</span>
              <span className="s-meta">{[w.publisher, maandJaar(w.datum)].filter(Boolean).join(" · ")}</span>
            </button>
          </li>
        ))}
      </ul>
      {d.totaal > d.regels.length && (
        <p className="s-meta">
          {d.regels.length} van {d.totaal} bevindingen onder Waarom nu
        </p>
      )}
    </div>
  );
}

const LaagTrendKaart: KaartComponent = ({ id, titel, toon }) => <LaagTrendLader id={id} titel={titel} toon={toon} />;

export function LaagTrendLader({ id, titel, toon }: Parameters<KaartComponent>[0]) {
  const r = useResource(() => trendVanDezeWeergave(id), [id]);
  const t = r.data ?? null;
  return toon({
    label: t?.titel ?? titel ?? "Trend",
    kop: (
      <span className="s-meta">
        Trend{t ? ` · ${t.onderwerp}` : ""}
        {t?.opkomend ? " · opkomend" : ""}
      </span>
    ),
    inhoud: t ? (
      <TrendInhoud t={t} />
    ) : r.error ? (
      <LegeStaat zin={`De trend kon niet worden opgehaald: ${r.error}`} actie="Opnieuw proberen" onActie={r.reload} />
    ) : r.loading ? (
      <LaadRegels regels={4} zin="Trend laden…" />
    ) : (
      <LegeStaat zin="Deze trend staat niet (meer) in de trendlaag." />
    ),
  });
}

const OntwikkelingKaart: KaartComponent = ({ id, titel, toon }) => <OntwikkelingLader id={id} titel={titel} toon={toon} />;

function OntwikkelingLader({ id, titel, toon }: Parameters<KaartComponent>[0]) {
  const r = useResource(() => api.trendlaagOntwikkeling(id), [id]);
  const plan = usePlanModus();
  const o = r.data ?? null;
  return toon({
    label: o?.titel ?? titel ?? "Ontwikkeling",
    kop: <span className="s-meta">Ontwikkeling{o?.trendTitel ? ` · ${o.trendTitel}` : ""}</span>,
    inhoud: o ? (
      <div className="tl-kaart">
        {/* De eigen titel bovenaan; de trend staat in de kop erboven. */}
        <h2 className="tl-kaart__titel">{o.titel}</h2>
        {o.werkzin && <p className="tl-sub">{o.werkzin}</p>}
        <p className="s-meta">{ontwMeta(o)}</p>
        <h4 className="tl-blokkop">Bevindingen</h4>
        <ul className="tl-bev">
          {o.bevindingen.map((b) => (
            <li key={b.id}>
              <span className="tl-ontw__titel">{b.titel}</span>
              {b.quote && <q className="tl-citaat">{b.quote}</q>}
              <span className="s-meta">{[b.publisher, maandJaar(b.datum)].filter(Boolean).join(" · ")}</span>
            </li>
          ))}
        </ul>
      </div>
    ) : r.error ? (
      <LegeStaat zin={`De ontwikkeling kon niet worden opgehaald: ${r.error}`} actie="Opnieuw proberen" onActie={r.reload} />
    ) : (
      <LaadRegels regels={4} zin="Ontwikkeling laden…" />
    ),
    // Alleen in plan-modus een voet; anders blijft de kaart zoals hij was.
    voet: plan.actief ? <PlanVoet id={id} /> : undefined,
  });
}

/** Eén bevinding (een Waarom nu-regel of een bevinding uit een ontwikkeling):
 *  titel, één zin, de bronnen met citaat, en in de voet "Hoort bij: trend ›
 *  ontwikkeling" met een klik naar die kaart. Leest de lichte route
 *  /trendlaag/bevinding, die ook kandidaten zonder tldr of scores toont. */
export function BevindingLader({ id, titel, toon }: Parameters<KaartComponent>[0]) {
  const r = useResource(() => api.trendlaagBevinding(id), [id]);
  const open = useOpenKaart();
  const b = r.data ?? null;
  return toon({
    label: b?.titel ?? titel ?? "Bevinding",
    kop: <span className="s-meta">Bevinding{b?.onderwerp ? ` · ${b.onderwerp}` : ""}</span>,
    inhoud: b ? (
      <div className="tl-kaart">
        <h2 className="tl-kaart__titel">{b.titel}</h2>
        {b.zin && <p className="tl-sub">{b.zin}</p>}
        {b.peildatum && <p className="s-meta">Peildatum: {b.peildatum}</p>}
        <h4 className="tl-blokkop">{b.bronnen.length === 1 ? "Bron" : `Bronnen (${b.bronnen.length})`}</h4>
        {b.bronnen.length === 0 ? (
          <p className="s-meta">Bij deze bevinding staat geen citaat.</p>
        ) : (
          <ul className="tl-bev">
            {b.bronnen.map((e, i) => (
              <li key={`${e.url ?? ""}-${i}`}>
                {e.quote && <q className="tl-citaat">{e.quote}</q>}
                <span className="s-meta">
                  {[e.publisher, maandJaar(e.datum)].filter(Boolean).join(" · ")}
                  {e.url && (
                    <>
                      {" · "}
                      <a href={e.url} target="_blank" rel="noreferrer" aria-label={`Bron openen: ${e.publisher ?? e.url}`}>
                        bron <Icoon als={ExternalLink} klein />
                      </a>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    ) : r.error ? (
      <LegeStaat zin={`De bevinding kon niet worden opgehaald: ${r.error}`} actie="Opnieuw proberen" onActie={r.reload} />
    ) : (
      <LaadRegels regels={4} zin="Bevinding laden…" />
    ),
    voet:
      b?.trendId && b.trendTitel ? (
        <p className="s-meta" style={{ margin: 0 }}>
          Hoort bij:{" "}
          <Knop variant="link" style={LINK_IN_ZIN} onClick={() => open({ soort: "laagtrend", id: b.trendId!, titel: b.trendTitel ?? undefined })}>
            {b.trendTitel}
          </Knop>
          {b.ontwikkelingId && b.ontwikkelingTitel && (
            <>
              <span aria-hidden="true"> › </span>
              <Knop variant="link" style={LINK_IN_ZIN} onClick={() => open({ soort: "ontwikkeling", id: b.ontwikkelingId!, titel: b.ontwikkelingTitel ?? undefined })}>
                {b.ontwikkelingTitel}
              </Knop>
            </>
          )}
        </p>
      ) : undefined,
  });
}

// Een knop als link midden in een zin: mag over regels breken (lange trendnaam
// op 375 px), in plaats van de s-knop-regel "nowrap".
const LINK_IN_ZIN = { display: "inline", whiteSpace: "normal", textAlign: "left" } as const;

registreerKaart("laagtrend", LaagTrendKaart);
registreerKaart("ontwikkeling", OntwikkelingKaart);
